/**
 * Shared display helpers for interaction rows.
 *
 * Used by every surface that renders a ProcessedInteraction — Me tab
 * feed, Contact detail Interactions tab, and the InteractionDetail
 * sheet itself. Pure functions so there's no React coupling; features
 * import them directly without wiring hooks.
 */
import type { Contact } from "@/lib/data";

/**
 * Resolve a contactId (Apple Contacts identifier OR the reserved
 * "user_self") to a display name. Falls back to a short truncation
 * of the id so the UI never shows an empty string.
 */
export function displayNameForContactId(
  contactId: string,
  contacts: Contact[]
): string {
  if (contactId === "user_self") return "You";
  const contact = contacts.find(
    (c) => c.appleContactId === contactId || c.id === contactId
  );
  if (contact) {
    const full = `${contact.firstName ?? ""} ${contact.lastName ?? ""}`.trim();
    return full || "Unknown";
  }
  return contactId.length > 10 ? `${contactId.slice(0, 8)}…` : contactId;
}

/** Coarse relative-time label for interaction feeds. */
export function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const delta = Math.max(0, Date.now() - d.getTime());
  const seconds = Math.floor(delta / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Humanize a duration in seconds — short convos as seconds, longer in minutes. */
export function formatInteractionDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0s";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} min`;
}
